import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Container, Row, Col, Table, Card, CardBody } from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import TableShimmer from "components/Shared/TableShimmer"
import { getB2Ccategories } from "store/actions/categoriesAction"
import { getB2CItemsListDropdown } from "store/actions/itemsAction"
import { getAllB2CSummarySalesReports } from "store/actions/reportsAction"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
const dummyData = {
  columns: [
    {
      label: "Order Number",
    },
    {
      label: "Order Value before VAT",
    },
    {
      label: "VAT",
    },
    {
      label: "Order Value after VAT",
    },
    {
      label: "Shipping Value",
    },
    {
      label: "Order Value Without Shipping",
    },
    {
      label: "Payment Type",
    },
    {
      label: "Total Paid Amount",
    },
    {
      label: "Order Date",
    },
  ],
}

const formatDateTime = date => {
  const pad = value => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const SummarySalesReport = () => {
  const navigate = useNavigate()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()

  document.title = t("Summary Sales Reports ")

  const flatpickrRef = useRef(null)
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      45,
      "estore_report_1",
      accountType
    )

    const subPermission = getSubPermissions(
      userRole,
      45,
      "estore_report_1",
      accountType
    )

    const isAllowed =
      isPermissionList && subPermission?.view_summary_sales?.isEnabled

    if (!isAllowed) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isAllowed,
    })
  }, [userRole])

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    b2cSummarySalesReports,
  } = useSelector(state => state.Reports)
  const { b2cCategories } = useSelector(state => state.Categories)
  const { b2cItemsListDropdown } = useSelector(state => state.Items)

  const fetchB2CSummarySalesReport = async () => {
    const hasDateRange = dateRange?.min && dateRange?.max
    dispatch(
      getAllB2CSummarySalesReports({
        perPage: 10,
        pageNo: 1,
        dateRange: hasDateRange
          ? {
              min: dateRange.min,
              max: dateRange.max,
            }
          : {},
      })
    )
  }

  //   Initial Load
  useEffect(() => {
    fetchB2CSummarySalesReport()
    dispatch(getB2Ccategories())
    dispatch(getB2CItemsListDropdown())
  }, [])

  //** Generating Payload */
  const generatePayload = page => {
    return {
      dateRange:
        dateRange?.min && dateRange?.max
          ? {
              min: dateRange.min,
              max: dateRange.max,
            }
          : {},
      perPage: itemsPerPage,
      pageNo: page,
    }
  }

  //   On Page Change
  const onPageChange = page => {
    const payload = generatePayload(page)
    dispatch(getAllB2CSummarySalesReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/e-store/summary-sales-report?page=${page}`)
        }
      })
      .catch(error => {})
  }

  //** Filters  */
  let filters = []

  const handleFilterChange = (selectedValue, filterName) => {
    // setSelectedFilters(prevFilters => ({
    //   ...prevFilters,
    //   [filterName]: selectedValue,
    // }))
  }

  const handleReset = () => {
    const resetDateRange = getDefaultDateRange()
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      dateRange: resetDateRange,
    }
    dispatch(getAllB2CSummarySalesReports(payload))
    navigate(`/e-store/summary-sales-report?page=${1}`)
    setDateRange(resetDateRange)
    if (flatpickrRef.current && flatpickrRef.current.flatpickr) {
      flatpickrRef.current.flatpickr.setDate(
        [resetDateRange.min, resetDateRange.max],
        true
      )
    }
  }

  //** Search  */
  const handleSearch = () => {
    const payload = generatePayload(1)
    if (payload) {
      dispatch(getAllB2CSummarySalesReports(payload))
      navigate(`/e-store/summary-sales-report?page=${1}`)
    }
  }
  const exportToCSV = async () => {
    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "SUMMARY-SALES-REPORT",
      exportType: "csv",
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      const fileName = hasDateRange
        ? t(`summary_sales_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Summary Sales Report Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "SUMMARY-SALES-REPORT",
      exportType: "xlsx",
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const fileName = hasDateRange
        ? t(`summary_sales_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Summary Sales Report Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={`${t("Summary Sales Report")}`}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  handleSearch={handleSearch}
                  filters={filters}
                  hideSearch={true}
                  handleFilterChange={handleFilterChange}
                  onReset={handleReset}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  setDateRange={setDateRange}
                  colSize={4}
                />

                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : b2cSummarySalesReports?.length === 0 ? (
                    <p className="text-center my-3">
                      {t("No sales reports found")}
                    </p>
                  ) : (
                    <Table className="table mb-0 " hover>
                      <thead className="table-light full_th">
                        <tr>
                          {dummyData.columns?.map(column => {
                            return <th key={column.key}>{t(column.label)}</th>
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {b2cSummarySalesReports?.map((order, orderIndex) => (
                          <tr key={`${order._id}`} className="capitalize_td">
                            {/* Order Number */}
                            <td>{order?.invoiceNo}</td>

                            {/* SKU Number */}
                            <td>{order?.valueBeforeVat || "-"}</td>

                            {/* Item Name */}
                            <td>{order?.vat}</td>

                            {/* Quantity */}
                            <td>{order.valueAfterVat}</td>

                            {/* Item Value */}
                            <td>{order.shippingValue}</td>
                            <td>{order.valueWithoutShipping}</td>
                            <td>{order.paymentType}</td>
                            <td>{order.totalPaidAmount}</td>

                            {/* Order Date */}
                            <td>
                              {new Date(order.orderDate).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>

                {!loading && b2cSummarySalesReports?.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={b2cSummarySalesReports}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default SummarySalesReport
